import { Activity, Brain, CalendarClock, Code2, FolderTree, GitBranch, Globe, MoreHorizontal, Sparkles, StickyNote, TerminalSquare, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DockTenant } from "@/features/dock/Dock.tsx";
import { cn } from "@/lib/cn.ts";

/** Shown as icons next to the menu on iPad. */
const QUICK: (DockTenant | "ide")[] = ["providers", "terminal", "files", "browser"];
const ITEMS: { tenant: DockTenant | "ide"; icon: typeof Globe; soon?: boolean }[] = [
  { tenant: "ide", icon: Code2 },
  { tenant: "team", icon: Users },
  { tenant: "providers", icon: Sparkles },
  { tenant: "terminal", icon: TerminalSquare },
  { tenant: "files", icon: FolderTree },
  { tenant: "git", icon: GitBranch },
  { tenant: "note", icon: StickyNote },
  { tenant: "memory", icon: Brain },
  { tenant: "browser", icon: Globe },
  { tenant: "schedules", icon: CalendarClock },
  { tenant: "services", icon: Activity },
];

/** The "workspace actions" menu from the chat header: opens a dock tenant. */
/** `hideTeam`: the Chat section talks to providers directly: its "Providers" tool takes the team's place. */
export function WorkspaceActions({ onOpen, onIde, active, compact = false, teamCount, hideTeam = false }: { onOpen: (t: DockTenant) => void; onIde?: (() => void) | undefined; active: DockTenant | null; compact?: boolean; teamCount?: number | undefined; hideTeam?: boolean }) {
  const { t } = useTranslation();
  // the Chat section runs no apps of its own: no services there
  const items = ITEMS.filter((i) => (hideTeam ? i.tenant !== "team" && i.tenant !== "services" : i.tenant !== "providers"));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      {/* sized by the chat header (container query), not the screen: a docked panel narrows the header.
          wide: icons with labels; medium: icons only; narrow: three quick tools and a menu */}
      <div className="hidden items-center gap-0.5 rounded-lg border border-border bg-surface-2/40 p-0.5 @2xl:flex">
        {items.map((i) => (
          <button key={i.tenant} type="button" disabled={i.soon || (i.tenant === "ide" && !onIde)} title={t(`dock.${i.tenant}`)} aria-label={t(`dock.${i.tenant}`)} onClick={() => (i.tenant === "ide" ? onIde?.() : onOpen(i.tenant))} className={cn("flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-40", active === i.tenant && "bg-accent-soft text-fg", (i.tenant === "team" || i.tenant === "providers") && active !== i.tenant && "text-accent")}>
            <i.icon className="size-4" />
            {!compact && <span className="hidden whitespace-nowrap @5xl:inline">{t(`dock.${i.tenant}`)}</span>}
            {(i.tenant === "team" || i.tenant === "providers") && teamCount !== undefined && <span className="rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-fg">{teamCount}</span>}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-0.5 @2xl:hidden">
        {/* iPad: the three most used tools stay one tap away */}
        {items.filter((i) => QUICK.includes(i.tenant)).map((i) => (
          <button key={i.tenant} type="button" title={t(`dock.${i.tenant}`)} aria-label={t(`dock.${i.tenant}`)} onClick={() => onOpen(i.tenant as DockTenant)} className={cn("tap hidden place-items-center rounded-md p-2 text-fg-muted hover:bg-surface-2 hover:text-fg md:grid", active === i.tenant && "bg-accent-soft text-fg")}>
            <i.icon className="size-4" />
          </button>
        ))}
        <button type="button" aria-label={t("dock.actions")} aria-expanded={open} onClick={() => setOpen(!open)} className="tap grid place-items-center rounded-md p-2 text-fg-muted hover:bg-surface-2 hover:text-fg"><MoreHorizontal className="size-5" /></button>
      </div>
      {open && (
        <div className="absolute end-0 top-11 z-40 w-60 rounded-lg border border-border bg-surface py-1 text-sm shadow-xl">
          {items.map((i) => (
            <button key={i.tenant} type="button" disabled={i.soon || (i.tenant === "ide" && !onIde)} onClick={() => { if (i.tenant === "ide") onIde?.(); else onOpen(i.tenant); setOpen(false); }} className={cn("flex w-full items-center gap-3 px-3 py-2.5 text-start hover:bg-surface-2 disabled:opacity-50", active === i.tenant && "text-accent")}>
              <i.icon className="size-4" /> {t(`dock.${i.tenant}`)} {i.soon && <span className="ms-auto rounded bg-surface-2 px-1 text-[10px] text-fg-muted">{t("common.soon")}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
